// @file For modes on tldraw canvas

import { trackEvent } from '@@/bits/analytics'
import { clearSearchParam, setSearchParam } from '@@/bits/location'
import { sfxManager } from '@@/bits/sfx'
import { vfxManager } from '@@/bits/vfx'
import { useSurfaceStore } from '@@/pinia/surface'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum WhiteboardMode {
  Draw,
  Code,
  Play,
}

export const useWhiteboardModeStore = defineStore('whiteboardMode', () => {
  const surfaceStore = useSurfaceStore()

  const searchParams = new URLSearchParams(window.location.search)
  const isScreenshotMode = ref((searchParams.get('screenshot') ?? '') === '1')
  const isFramePreviewMode = ref((searchParams.get('frame_preview') ?? '') === '1')
  const isFullWidthFramePreviewMode = ref((searchParams.get('full_width_frame_preview') ?? '') === '1')

  const currentMode = ref((searchParams.get('play') ?? '') === '1' ? WhiteboardMode.Play : WhiteboardMode.Draw)

  function setMode(mode: typeof WhiteboardMode[keyof typeof WhiteboardMode]): void {
    if (currentMode.value === WhiteboardMode.Play && mode !== WhiteboardMode.Play) {
      vfxManager.stopAllEffects()
      sfxManager.stopAllEffects()
    }
    currentMode.value = mode
  }

  const isDrawMode = computed(() => currentMode.value === WhiteboardMode.Draw)
  const isCodeMode = computed(() => currentMode.value === WhiteboardMode.Code)
  const isPlayMode = computed(() => currentMode.value === WhiteboardMode.Play)

  // Explicitly keep this decoupled from canIXFrame because this is a global state that doesn't care about permissions,
  // canIXFrame relates to whether the user has the permissions.
  const isReadOnlyMode = computed(() => {
    return (
      isPlayMode.value ||
      isScreenshotMode.value ||
      surfaceStore.isFrozen ||
      isFramePreviewMode.value ||
      isFullWidthFramePreviewMode.value
    )
  })

  function enterPlayMode(): void {
    trackEvent('Whiteboard', 'Changed mode', 'Play mode', null, { context: 'Mode switcher' })

    setMode(WhiteboardMode.Play)
    // Set search params
    setSearchParam('play', '1')
  }

  function enterDrawMode(): void {
    trackEvent('Whiteboard', 'Changed mode', 'Draw mode', null, { context: 'Mode switcher' })

    setMode(WhiteboardMode.Draw)
    // Clear search params
    clearSearchParam('play')
  }

  function enterCodeMode(): void {
    trackEvent('Whiteboard', 'Changed mode', 'Code mode', null, { context: 'Mode switcher' })

    setMode(WhiteboardMode.Code)
    // Clear search params
    clearSearchParam('play')
  }

  return {
    currentMode,
    setMode,
    isDrawMode,
    isCodeMode,
    isPlayMode,
    isReadOnlyMode,
    isScreenshotMode,
    isFramePreviewMode,
    isFullWidthFramePreviewMode,
    enterPlayMode,
    enterDrawMode,
    enterCodeMode,
  }
})
